import React, { ReactNode, useLayoutEffect, useRef, useState } from "react";

import Box from "veneer/Box";
import Button from "veneer/Button";
import Chip from "veneer/Chip";
import ClickAwayListener from "veneer/ClickAwayListener";
import Collapse from "veneer/Collapse";
import Divider from "veneer/Divider";
import { IconButton } from "veneer/IconButton";
import PaperPopper from "veneer/PaperPopper";
import Stack from "veneer/Stack";
import { ExpandLessFilledIcon, ExpandMoreFilledIcon } from "veneer/SvgIcon";
import styled from "veneer/styled";
import TextInput from "veneer/TextInput";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import { createDataTestId } from "vistar/ui/test_helpers/dataTestId";

export interface AppliedFilter {
  key: string;
  label: ReactNode;
  onDelete?: () => void;
  value: string | number;
}

export type SaveAppliedFiltersHandler = (saveSetName: string) => boolean | void;

interface TableAppliedFiltersProps {
  dataTestId?: string;
  filters: ReadonlyArray<AppliedFilter>;
  onClearFilters: () => void;
  onSave?: SaveAppliedFiltersHandler;
  saveDialogMessage?: ReactNode;
}

interface ChipLabelProps {
  label: ReactNode;
  value: string | number;
}

interface FilterChipProps {
  dataTestId?: string;
  filter: AppliedFilter;
}

interface FilterActionsProps {
  dataTestId: string;
  isExpanded: boolean;
  isMultiRow: boolean;
  onClearFilters: () => void;
  onSave?: SaveAppliedFiltersHandler;
  onToggleExpand: () => void;
  saveDialogMessage?: ReactNode;
}

interface SaveAppliedFiltersActionProps {
  dataTestId: string;
  onSave: SaveAppliedFiltersHandler;
  saveDialogMessage?: ReactNode;
}

const DEFAULT_BORDER_RADIUS = 16;

const AppliedFiltersContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isExpanded" && prop !== "isMultiRow",
})<{ isExpanded: boolean; isMultiRow: boolean }>(
  ({ isExpanded, isMultiRow, theme }) => ({
    alignItems: "flex-start",
    backgroundColor: Colors.blue100,
    borderRadius:
      isMultiRow && isExpanded
        ? DEFAULT_BORDER_RADIUS
        : DEFAULT_BORDER_RADIUS * 2,
    display: "flex",
    gap: theme.spacing(2),
    margin: theme.spacing(0, 2, 2),
    padding: theme.spacing(1, 2),
  }),
);

const ChipArea = styled(Box)({
  flex: "1 1 auto",
  minWidth: 0,
  position: "relative",
});

const ChipList = styled(Stack)(({ theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(0.75),
  flexDirection: "row",
  flexWrap: "wrap",
  minWidth: 0,
  overflow: "hidden",
  rowGap: theme.spacing(0.75),
}));

const MeasurementChipList = styled(ChipList)({
  left: 0,
  pointerEvents: "none",
  position: "absolute",
  top: 0,
  visibility: "hidden",
  width: "100%",
});

const HiddenFilterCount = styled(Typography)({
  flexShrink: 0,
});

const MeasurementHiddenFilterCount = styled(HiddenFilterCount)({
  left: 0,
  pointerEvents: "none",
  position: "absolute",
  top: 0,
  visibility: "hidden",
  whiteSpace: "nowrap",
});

const ExpandDivider = styled(Divider)({
  alignSelf: "center",
  borderColor: Colors.blue750,
  height: 20,
});

export default function TableAppliedFilters({
  dataTestId = "table-applied-filters",
  filters,
  onClearFilters,
  onSave,
  saveDialogMessage,
}: TableAppliedFiltersProps) {
  const [showCollapsedFilters, setShowCollapsedFilters] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMultiRow, setIsMultiRow] = useState(false);
  const [visibleFilterCount, setVisibleFilterCount] = useState(0);
  const measurementListRef = useRef<HTMLDivElement>(null);
  const hiddenCountMeasurementRef = useRef<HTMLDivElement>(null);
  const displayedFilters = showCollapsedFilters
    ? filters.slice(0, visibleFilterCount)
    : filters;
  const hiddenFilterCount = Math.max(filters.length - visibleFilterCount, 0);

  useLayoutEffect(() => {
    const measurementList = measurementListRef.current;
    if (!measurementList) {
      return undefined;
    }

    const measureFilters = () => {
      const chips = Array.from(measurementList.children) as HTMLElement[];
      if (chips.length === 0) {
        setIsMultiRow(false);
        setVisibleFilterCount(0);
        return;
      }

      const firstRowTop = chips[0].offsetTop;
      const firstRowCount = chips.filter(
        (chip) => chip.offsetTop === firstRowTop,
      ).length;
      const hasMultipleRows = chips.some(
        (chip) => chip.offsetTop > firstRowTop,
      );
      if (!hasMultipleRows) {
        setIsExpanded(false);
        setShowCollapsedFilters(true);
      }
      const hiddenCountBadgeWidth =
        hiddenCountMeasurementRef.current?.offsetWidth ?? 0;
      const styles = window.getComputedStyle(measurementList);
      const measuredGap = Number.parseFloat(
        styles.columnGap || styles.gap || "0",
      );
      const horizontalGap = Number.isFinite(measuredGap) ? measuredGap : 0;

      const getFirstRowWidth = (chipCount: number) => {
        const chipsWidth = chips
          .slice(0, chipCount)
          .reduce((total, chip) => total + chip.offsetWidth, 0);
        const gapsWidth = horizontalGap * chipCount;

        return chipsWidth + gapsWidth + hiddenCountBadgeWidth;
      };

      let visibleCount = chips.length;
      if (hasMultipleRows) {
        visibleCount = 1;
        for (let candidate = firstRowCount; candidate >= 1; candidate -= 1) {
          if (getFirstRowWidth(candidate) <= measurementList.clientWidth) {
            visibleCount = candidate;
            break;
          }
        }
      }
      setIsMultiRow(hasMultipleRows);
      setVisibleFilterCount(visibleCount);
    };

    measureFilters();

    const resizeObserver = new ResizeObserver(measureFilters);
    resizeObserver.observe(measurementList);

    return () => resizeObserver.disconnect();
  }, [filters]);

  if (filters.length === 0) {
    return null;
  }

  const handleToggleExpand = () => {
    if (isExpanded) {
      setIsExpanded(false);
    } else {
      setShowCollapsedFilters(false);
      setIsExpanded(true);
    }
  };

  return (
    <AppliedFiltersContainer
      dataTestId={dataTestId}
      isExpanded={isExpanded}
      isMultiRow={isMultiRow}
    >
      <Box alignItems="center" display="flex" minHeight={32}>
        <Typography
          color={Colors.gray1000}
          flexShrink={0}
          noWrap
          variant="body2"
        >
          Applied Filters:
        </Typography>
      </Box>
      <ChipArea>
        <Collapse
          collapsedSize={32}
          expanded={isExpanded}
          onExited={() => setShowCollapsedFilters(true)}
        >
          <ChipList>
            {displayedFilters.map((filter) => (
              <FilterChip
                dataTestId={dataTestId}
                filter={filter}
                key={filter.key}
              />
            ))}
            {showCollapsedFilters && hiddenFilterCount > 0 && (
              <HiddenFilterCount color={Colors.gray1000} noWrap variant="body2">
                +{hiddenFilterCount}
              </HiddenFilterCount>
            )}
          </ChipList>
        </Collapse>
        <MeasurementChipList aria-hidden="true" ref={measurementListRef}>
          {filters.map((filter) => (
            <FilterChip filter={filter} key={filter.key} />
          ))}
        </MeasurementChipList>
        <MeasurementHiddenFilterCount
          color={Colors.gray1000}
          noWrap
          ref={hiddenCountMeasurementRef}
          variant="body2"
        >
          +{filters.length}
        </MeasurementHiddenFilterCount>
      </ChipArea>
      <FilterActions
        dataTestId={dataTestId}
        isExpanded={isExpanded}
        isMultiRow={isMultiRow}
        onClearFilters={onClearFilters}
        onSave={onSave}
        onToggleExpand={handleToggleExpand}
        saveDialogMessage={saveDialogMessage}
      />
    </AppliedFiltersContainer>
  );
}

function ChipLabel({ label, value }: ChipLabelProps) {
  return (
    <Stack alignItems="center" direction="row" spacing={0.5}>
      <Typography variant="body2">{label}: </Typography>
      <Typography color={Colors.gray1000} variant="body2">
        {value}
      </Typography>
    </Stack>
  );
}

function FilterChip({ dataTestId, filter }: FilterChipProps) {
  return (
    <Chip
      dataTestId={
        dataTestId
          ? createDataTestId([dataTestId, "filter", filter.key])
          : undefined
      }
      label={<ChipLabel label={filter.label} value={filter.value} />}
      onDelete={filter.onDelete}
      size="medium"
    />
  );
}

function FilterActions({
  dataTestId,
  isExpanded,
  isMultiRow,
  onClearFilters,
  onSave,
  onToggleExpand,
  saveDialogMessage,
}: FilterActionsProps) {
  return (
    <Stack
      alignItems="center"
      direction="row"
      divider={<ExpandDivider orientation="vertical" />}
      spacing={1}
    >
      <Stack alignItems="center" direction="row" spacing={1}>
        <Button
          dataTestId={`${dataTestId}-reset-all`}
          onClick={onClearFilters}
          variant="text"
        >
          Reset All
        </Button>
        {onSave && (
          <SaveAppliedFiltersAction
            dataTestId={dataTestId}
            onSave={onSave}
            saveDialogMessage={saveDialogMessage}
          />
        )}
      </Stack>
      {isMultiRow && (
        <IconButton
          dataTestId={`${dataTestId}-toggle-expand`}
          icon={
            isExpanded ? (
              <ExpandLessFilledIcon htmlColor={Colors.blue700} />
            ) : (
              <ExpandMoreFilledIcon htmlColor={Colors.blue700} />
            )
          }
          onClick={onToggleExpand}
          size="small"
          title={isExpanded ? "Collapse" : "Expand"}
        />
      )}
    </Stack>
  );
}

function SaveAppliedFiltersAction({
  dataTestId,
  onSave,
  saveDialogMessage,
}: SaveAppliedFiltersActionProps) {
  const [openSavePopper, setOpenSavePopper] = useState(false);
  const [saveSetName, setSaveSetName] = useState("");
  const [saveSetNameError, setSaveSetNameError] = useState<string>();
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleSave = () => {
    if (!saveSetName.trim()) {
      setSaveSetNameError("This field is required.");
      return;
    }

    if (onSave(saveSetName) === false) {
      setSaveSetNameError("A saved filter set with this name already exists.");
      return;
    }

    setSaveSetName("");
    setSaveSetNameError(undefined);
    setOpenSavePopper(false);
  };

  const handleCancelSave = () => {
    setSaveSetName("");
    setSaveSetNameError(undefined);
    setOpenSavePopper(false);
  };

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    handleCancelSave();
  };

  return (
    <div ref={anchorRef}>
      <Button
        dataTestId={`${dataTestId}-save`}
        onClick={() => setOpenSavePopper(true)}
        variant="text"
      >
        Save
      </Button>
      {openSavePopper && (
        <PaperPopper
          anchorEl={anchorRef.current}
          borderRadius={2}
          open={openSavePopper}
        >
          <ClickAwayListener onClickAway={handleClickAway}>
            <Box p={2} width={400}>
              <Stack gap={3}>
                <Stack gap={3} p={1}>
                  <Typography color={Colors.gray1000} variant="h4">
                    Save Applied Filters Set
                  </Typography>
                  <TextInput
                    autoFocus
                    dataTestId={`${dataTestId}-save-name`}
                    error={Boolean(saveSetNameError)}
                    fullWidth
                    helperText={saveSetNameError}
                    label="Name"
                    onChange={(value) => {
                      const nextSaveSetName = value ?? "";
                      setSaveSetName(nextSaveSetName);
                      setSaveSetNameError(undefined);
                    }}
                    value={saveSetName}
                  />
                  {saveDialogMessage}
                </Stack>
                <Stack direction="row" gap={1} justifyContent="flex-end">
                  <Button
                    dataTestId={`${dataTestId}-save-cancel`}
                    onClick={handleCancelSave}
                    variant="outlined"
                  >
                    Cancel
                  </Button>
                  <Button
                    dataTestId={`${dataTestId}-save-confirm`}
                    onClick={handleSave}
                  >
                    Save
                  </Button>
                </Stack>
              </Stack>
            </Box>
          </ClickAwayListener>
        </PaperPopper>
      )}
    </div>
  );
}
